import { findProductByPartNo, isHardIoCapabilityEligible } from "@automatry/catalog";
import { deriveScreenMapping, getScreenDeviceProduct } from "@automatry/rules";
import { ImpactPreviewSchema, OverrideDraftSchema, defaultEdgeRemovalTargetRef, isDefaultEdgeRemovalSelector, shouldInjectDefaultEdgeRows } from "@automatry/schemas";
import { getCatalogProducts, zeroPoints } from "./controllerAllocation.js";
import { deterministicHash } from "./hash.js";
import { buildApartmentPointLedger, buildControllerPointLedger } from "./pointLedger.js";
import { ENGINE_TIMESTAMP, NO_RISK, uniqueSourceRefs } from "./strategyContext.js";
export function applyScopedOverridesToSolution(input) {
    const solutionDraft = input.solution;
    const applicableOverrides = getApplicableOverrides({
        ...input,
        solution: solutionDraft
    });
    if (applicableOverrides.length === 0) {
        return {
            solution: input.solution,
            appliedOverrideIds: []
        };
    }
    let solution = structuredClone(solutionDraft);
    const catalogProducts = getCatalogProducts(input.catalog);
    const appliedOverrideIds = [];
    for (const override of applicableOverrides) {
        const result = applyOneOverride({
            solution,
            override,
            catalogProducts,
            projectId: input.projectId
        });
        solution = result.solution;
        if (result.appliedOverrideIds.includes(override.id)) {
            appliedOverrideIds.push(override.id);
        }
    }
    return {
        solution,
        appliedOverrideIds
    };
}
export function buildImpactPreview(input) {
    const beforeSolutions = input.before.apartmentSolutions;
    const afterSolutions = input.after.apartmentSolutions;
    const validationFindings = input.after.validation.findings;
    const changes = [
        ...buildProductQuantityChanges(input.before, input.after),
        ...buildValidationStatusChanges(input.before, input.after),
        ...buildRiskCountChanges(input.before, input.after)
    ];
    const affectedApartmentTypeIds = afterSolutions
        .filter((afterSolution) => {
        const beforeSolution = beforeSolutions.find((solution) => solution.apartmentTypeId === afterSolution.apartmentTypeId);
        return deterministicHash(beforeSolution) !== deterministicHash(afterSolution);
    })
        .map((solution) => solution.apartmentTypeId);
    const affectedProductRefs = changes
        .filter((change) => change.kind === "product_quantity")
        .map((change) => change.subjectRef);
    const affectedMappingIds = input.after.apartmentSolutions.flatMap((solution) => solution.mappings
        .filter((mapping) => mapping.ruleTraceRefs.some((traceRef) => traceRef.includes(input.overrideDraftId)))
        .map((mapping) => mapping.id));
    const affectedRiskIds = [
        ...afterSolutions.flatMap((solution) => solution.risks.map((risk) => risk.id)),
        ...validationFindings
            .map((finding) => finding.subjectRef)
            .filter((subjectRef) => subjectRef !== undefined)
    ];
    const summary = changes.length === 0
        ? "No material before/after delta was detected for the override preview."
        : changes.map((change) => change.summary).join("; ");
    const validationStatus = input.after.validation.status === "accepted"
        ? "pass"
        : input.after.validation.status === "accepted_with_risks"
            ? "warning"
            : "fail";
    const idPayload = {
        overrideDraftId: input.overrideDraftId,
        before: input.before.id,
        after: input.after.id,
        changes
    };
    return ImpactPreviewSchema.parse({
        id: `impact-${deterministicHash(idPayload).slice(0, 12)}`,
        projectId: input.projectId,
        overrideDraftId: input.overrideDraftId,
        affectedRequirementIds: [],
        affectedApartmentTypeIds,
        affectedProductRefs,
        affectedMappingIds,
        affectedRiskIds: [...new Set(affectedRiskIds)],
        affectedExportIds: [],
        summary,
        validationStatus,
        changes,
        createdAt: input.createdAt ?? ENGINE_TIMESTAMP
    });
}
function getApplicableOverrides(input) {
    return (input.overrides ?? [])
        .map((override) => OverrideDraftSchema.parse(override))
        .filter((override) => override.projectId === input.projectId)
        .filter((override) => override.status === "ready")
        .filter((override) => scopeApplies(override, input.apartmentTypeId, input.solution))
        .sort((left, right) => {
        const createdAt = left.createdAt.localeCompare(right.createdAt);
        return createdAt === 0 ? left.id.localeCompare(right.id) : createdAt;
    });
}
function scopeApplies(override, apartmentTypeId, solution) {
    if (override.scope.level === "project") {
        return true;
    }
    if (override.scope.level === "apartment_type") {
        return override.scope.refId === apartmentTypeId;
    }
    if (!override.scope.refId) {
        return false;
    }
    if (override.scope.level === "device") {
        return solution.devices.some((device) => device.id === override.scope.refId);
    }
    if (override.scope.level === "mapping") {
        return (solution.mappings.some((mapping) => mapping.id === override.scope.refId) ||
            solution.physicalMappings.some((mapping) => mapping.id === override.scope.refId));
    }
    if (override.scope.level === "requirement") {
        return solution.controllers.some((controller) => controller.sourceRequirementIds.includes(override.scope.refId ?? ""));
    }
    return false;
}
function applyOneOverride(input) {
    const change = input.override.structuredChange;
    if ("operation" in change) {
        return applyLegacyOperation({
            ...input,
            change
        });
    }
    if (change.kind === "adjust_quantity") {
        return applyQuantityAdjustment({
            ...input,
            change
        });
    }
    if (change.kind === "substitute_product") {
        return applyProductSubstitution({
            ...input,
            change
        });
    }
    return applyStrategyInput({
        ...input,
        change
    });
}
function applyLegacyOperation(input) {
    if (input.change.operation === "add_io_module") {
        return {
            solution: addInfrastructureItem({
                solution: input.solution,
                override: input.override,
                catalogProducts: input.catalogProducts,
                itemKind: "io_module",
                productPartNo: input.change.productPartNo,
                quantity: input.change.quantity,
                capabilityRole: "io_expansion"
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    if (input.change.operation === "add_relay_module") {
        return {
            solution: addInfrastructureItem({
                solution: input.solution,
                override: input.override,
                catalogProducts: input.catalogProducts,
                itemKind: "io_module",
                productPartNo: input.change.productPartNo,
                quantity: input.change.quantity,
                capabilityRole: "load_relay_module"
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    return applyScreenQuantityOperation(input);
}
function applyQuantityAdjustment(input) {
    if (input.change.targetCollection === "infrastructure") {
        return applyInfrastructureQuantityAdjustment(input);
    }
    if (input.change.targetCollection === "devices") {
        return applyDeviceQuantityAdjustment(input);
    }
    return applyControllerQuantityAdjustment(input);
}
function applyProductSubstitution(input) {
    if (input.change.targetCollection === "devices") {
        return applyDeviceProductSubstitution(input);
    }
    if (input.change.targetCollection === "controllers") {
        return applyControllerProductSubstitution(input);
    }
    return applyInfrastructureProductSubstitution(input);
}
function applyStrategyInput(input) {
    if (input.change.input === "ui_screen_kind") {
        return applyDeviceProductSubstitution({
            ...input,
            change: {
                kind: "substitute_product",
                targetCollection: "devices",
                selector: {
                    role: "local_ui_ts4"
                },
                toProductPartNo: getScreenDeviceProduct(input.change.value).productPartNo,
                toScreenKind: input.change.value
            }
        });
    }
    const quantity = input.change.value.quantity ?? 1;
    const productPartNo = input.change.value.productPartNo ?? "TP-C-710R-IP";
    return {
        solution: recalculateControllerLedgers(addControllers({
            solution: input.solution,
            override: input.override,
            catalogProducts: input.catalogProducts,
            productPartNo,
            quantity
        })),
        appliedOverrideIds: [input.override.id]
    };
}
function applyInfrastructureQuantityAdjustment(input) {
    const targetIndex = input.solution.infrastructure.findIndex((item) => matchesInfrastructureSelector(item, input.change.selector));
    if (targetIndex === -1 && input.change.delta > 0) {
        const productPartNo = input.change.selector.productPartNo;
        const itemKind = input.change.selector.itemKind;
        if (productPartNo && isInfrastructureItemKind(itemKind)) {
            return {
                solution: addInfrastructureItem({
                    solution: input.solution,
                    override: input.override,
                    catalogProducts: input.catalogProducts,
                    itemKind,
                    productPartNo,
                    quantity: input.change.delta
                }),
                appliedOverrideIds: [input.override.id]
            };
        }
    }
    if (targetIndex === -1 &&
        isDefaultEdgeRemoval({
            solution: input.solution,
            change: input.change
        })) {
        return {
            solution: markDefaultEdgeRemoval({
                solution: input.solution,
                override: input.override,
                change: input.change
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    if (targetIndex === -1) {
        return {
            solution: addBlockingOverrideRisk({
                solution: input.solution,
                override: input.override,
                projectId: input.projectId,
                message: "Override targeted an infrastructure item that does not exist."
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    const infrastructure = [...input.solution.infrastructure];
    const target = infrastructure[targetIndex];
    if (!target) {
        return { solution: input.solution, appliedOverrideIds: [] };
    }
    const nextQuantity = target.quantity + input.change.delta;
    if (nextQuantity <= 0) {
        infrastructure.splice(targetIndex, 1);
    }
    else {
        infrastructure[targetIndex] = markOverridden({
            ...target,
            quantity: nextQuantity
        }, input.override);
    }
    return {
        solution: markSolution({
            ...input.solution,
            infrastructure
        }, input.override),
        appliedOverrideIds: [input.override.id]
    };
}
function applyDeviceQuantityAdjustment(input) {
    const targetIndex = input.solution.devices.findIndex((device) => matchesSelector(device, input.change.selector));
    if (targetIndex === -1) {
        return {
            solution: addBlockingOverrideRisk({
                solution: input.solution,
                override: input.override,
                projectId: input.projectId,
                message: "Override targeted a derived device that does not exist."
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    const devices = [...input.solution.devices];
    const target = devices[targetIndex];
    if (!target) {
        return { solution: input.solution, appliedOverrideIds: [] };
    }
    const nextQuantity = target.quantity + input.change.delta;
    const removedDeviceId = target.id;
    if (nextQuantity <= 0) {
        devices.splice(targetIndex, 1);
    }
    else {
        devices[targetIndex] = markOverridden({
            ...target,
            quantity: nextQuantity
        }, input.override);
    }
    return {
        solution: markSolution({
            ...input.solution,
            devices,
            mappings: nextQuantity <= 0
                ? input.solution.mappings.filter((mapping) => mapping.deviceRef !== removedDeviceId)
                : input.solution.mappings
        }, input.override),
        appliedOverrideIds: [input.override.id]
    };
}
function applyControllerQuantityAdjustment(input) {
    if (input.change.delta > 0) {
        const productPartNo = input.change.selector.productPartNo ?? "TP-C-710R-IP";
        return {
            solution: recalculateControllerLedgers(addControllers({
                solution: input.solution,
                override: input.override,
                catalogProducts: input.catalogProducts,
                productPartNo,
                quantity: input.change.delta
            })),
            appliedOverrideIds: [input.override.id]
        };
    }
    const removeCount = Math.abs(input.change.delta);
    const removalCandidates = input.solution.controllers
        .map((controller, index) => ({ controller, index }))
        .filter(({ controller }) => matchesSelector(controller, input.change.selector))
        .sort((left, right) => right.controller.id.localeCompare(left.controller.id));
    if (removalCandidates.length < removeCount) {
        return {
            solution: addBlockingOverrideRisk({
                solution: input.solution,
                override: input.override,
                projectId: input.projectId,
                message: "Override tried to remove more controllers than the selector matched."
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    const indexesToRemove = new Set(removalCandidates.slice(0, removeCount).map((candidate) => candidate.index));
    const controllers = input.solution.controllers.filter((_controller, index) => !indexesToRemove.has(index));
    return {
        solution: recalculateControllerLedgers(markSolution({
            ...input.solution,
            controllers
        }, input.override)),
        appliedOverrideIds: [input.override.id]
    };
}
function applyDeviceProductSubstitution(input) {
    const product = findProductByPartNo(input.catalogProducts, input.change.toProductPartNo);
    const screenKind = input.change.toScreenKind ?? inferScreenKind(input.change.toProductPartNo);
    const changedDeviceScreenKinds = new Map();
    const devices = input.solution.devices.map((device) => {
        if (!matchesProductSubstitutionSelector(device, input.change)) {
            return device;
        }
        const screenProduct = screenKind ? getScreenDeviceProduct(screenKind) : undefined;
        const nextDevice = markOverridden({
            ...device,
            productPartNo: input.change.toProductPartNo,
            family: screenProduct?.family ?? product?.family ?? device.family,
            role: screenProduct?.role ?? device.role,
            sourceRefs: product
                ? uniqueSourceRefs([...device.sourceRefs, ...product.sourceRefs])
                : device.sourceRefs,
            ruleTraceRefs: screenProduct
                ? uniqueRuleTraceRefs([...device.ruleTraceRefs, screenProduct.ruleTraceId])
                : device.ruleTraceRefs
        }, input.override);
        if (screenKind) {
            changedDeviceScreenKinds.set(device.id, screenKind);
        }
        return nextDevice;
    });
    if (changedDeviceScreenKinds.size === 0) {
        return {
            solution: addBlockingOverrideRisk({
                solution: input.solution,
                override: input.override,
                projectId: input.projectId,
                message: "Override targeted a derived device product that does not exist."
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    const nextSolution = markSolution({
        ...input.solution,
        devices,
        mappings: input.solution.mappings.map((mapping) => changedDeviceScreenKinds.has(mapping.deviceRef)
            ? substituteScreenMapping({
                mapping,
                screenKind: changedDeviceScreenKinds.get(mapping.deviceRef) ?? "ts4",
                solution: input.solution,
                override: input.override
            })
            : mapping)
    }, input.override);
    return {
        solution: nextSolution,
        appliedOverrideIds: [input.override.id]
    };
}
function applyControllerProductSubstitution(input) {
    const product = findProductByPartNo(input.catalogProducts, input.change.toProductPartNo);
    let changed = false;
    const controllers = input.solution.controllers.map((controller) => {
        if (!matchesProductSubstitutionSelector(controller, input.change)) {
            return controller;
        }
        changed = true;
        const sourceRefs = product
            ? uniqueSourceRefs([...controller.sourceRefs, ...product.sourceRefs])
            : controller.sourceRefs;
        const status = product && isHardIoCapabilityEligible(product) ? "accepted" : "needs_review";
        return markOverridden({
            ...controller,
            productPartNo: input.change.toProductPartNo,
            capacity: product?.capabilities.points ?? controller.capacity,
            status,
            sourceRefs,
            risk: product && isHardIoCapabilityEligible(product)
                ? NO_RISK
                : {
                    level: "blocker",
                    riskRefs: [`risk-${controller.id}-${input.override.id}-product`],
                    notes: "Override selected an unknown or unreviewed controller product."
                }
        }, input.override);
    });
    if (!changed) {
        return {
            solution: addBlockingOverrideRisk({
                solution: input.solution,
                override: input.override,
                projectId: input.projectId,
                message: "Override targeted a controller product that does not exist."
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    return {
        solution: recalculateControllerLedgers(markSolution({
            ...input.solution,
            controllers
        }, input.override)),
        appliedOverrideIds: [input.override.id]
    };
}
function applyInfrastructureProductSubstitution(input) {
    const product = findProductByPartNo(input.catalogProducts, input.change.toProductPartNo);
    let changed = false;
    const infrastructure = input.solution.infrastructure.map((item) => {
        if (!matchesProductSubstitutionSelector(item, input.change)) {
            return item;
        }
        changed = true;
        return markOverridden({
            ...item,
            productPartNo: input.change.toProductPartNo,
            ...(product
                ? {
                    supplySource: product.supplySource,
                    protocols: [...product.capabilities.protocols],
                    sourceStatus: product.sourceStatus,
                    sourceRefs: uniqueSourceRefs([...item.sourceRefs, ...product.sourceRefs])
                }
                : {})
        }, input.override);
    });
    if (!changed) {
        return {
            solution: addBlockingOverrideRisk({
                solution: input.solution,
                override: input.override,
                projectId: input.projectId,
                message: "Override targeted an infrastructure product that does not exist."
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    return {
        solution: markSolution({
            ...input.solution,
            infrastructure
        }, input.override),
        appliedOverrideIds: [input.override.id]
    };
}
function applyScreenQuantityOperation(input) {
    if (input.change.operation !== "add_screen" &&
        input.change.operation !== "remove_screen") {
        return { solution: input.solution, appliedOverrideIds: [] };
    }
    if (input.change.operation === "add_screen") {
        let solution = input.solution;
        for (let index = 0; index < input.change.quantity; index += 1) {
            solution = addScreenDevice({
                solution,
                override: input.override,
                screenKind: input.change.screenKind
            });
        }
        return {
            solution,
            appliedOverrideIds: [input.override.id]
        };
    }
    const screenProduct = getScreenDeviceProduct(input.change.screenKind);
    const removalCandidates = input.solution.devices
        .map((device, index) => ({ device, index }))
        .filter(({ device }) => device.productPartNo === screenProduct.productPartNo)
        .sort((left, right) => right.device.id.localeCompare(left.device.id));
    if (removalCandidates.length < input.change.quantity) {
        return {
            solution: addBlockingOverrideRisk({
                solution: input.solution,
                override: input.override,
                projectId: input.projectId,
                message: "Override tried to remove more screen devices than exist."
            }),
            appliedOverrideIds: [input.override.id]
        };
    }
    const indexesToRemove = new Set(removalCandidates
        .slice(0, input.change.quantity)
        .map((candidate) => candidate.index));
    const removedDeviceIds = new Set(removalCandidates
        .slice(0, input.change.quantity)
        .map((candidate) => candidate.device.id));
    return {
        solution: markSolution({
            ...input.solution,
            devices: input.solution.devices.filter((_device, index) => !indexesToRemove.has(index)),
            mappings: input.solution.mappings.filter((mapping) => !removedDeviceIds.has(mapping.deviceRef))
        }, input.override),
        appliedOverrideIds: [input.override.id]
    };
}
function addInfrastructureItem(input) {
    const product = findProductByPartNo(input.catalogProducts, input.productPartNo);
    const sourceRefs = product
        ? uniqueSourceRefs([...input.override.sourceRefs, ...product.sourceRefs])
        : input.override.sourceRefs;
    const item = markOverridden({
        id: `infrastructure-${input.solution.apartmentTypeId}-override-${safeId(input.override.id)}-${safeId(input.productPartNo)}`,
        itemKind: input.itemKind,
        supplySource: product?.supplySource ?? "titan_supplied",
        installIncluded: false,
        quantity: input.quantity,
        productPartNo: input.productPartNo,
        ...(product ? { protocols: [...product.capabilities.protocols] } : {}),
        selectionStatus: "selected",
        ...(input.capabilityRole ? { capabilityRole: input.capabilityRole } : {}),
        ...(product ? { sourceStatus: product.sourceStatus } : {}),
        why: `Overridden by ${input.override.id}: ${input.override.reason}`,
        how: "Scoped override added a derived infrastructure item after base derivation and before validation.",
        sourceRefs,
        ruleTraceRefs: [overrideTraceRef(input.override)],
        risk: NO_RISK
    }, input.override);
    return markSolution({
        ...input.solution,
        infrastructure: [...input.solution.infrastructure, item]
    }, input.override);
}
function addControllers(input) {
    const product = findProductByPartNo(input.catalogProducts, input.productPartNo);
    const capacity = product?.capabilities.points ?? zeroPoints();
    const sourceRefs = product
        ? uniqueSourceRefs([...input.override.sourceRefs, ...product.sourceRefs])
        : input.override.sourceRefs;
    const controllers = [...input.solution.controllers];
    for (let index = 0; index < input.quantity; index += 1) {
        const id = `controller-override-${safeId(input.override.id)}-${index + 1}`;
        const ruleTraceRefs = [overrideTraceRef(input.override)];
        const controller = markOverridden({
            id,
            productPartNo: input.productPartNo,
            role: "accessory",
            physicalLocation: "central_panel",
            zoneSlots: [],
            pointLedger: buildControllerPointLedger({
                controllerId: id,
                pointDemands: input.solution.pointDemands,
                zoneSlots: [],
                capacity,
                sourceRefs,
                ruleTraceRefs
            }),
            capacity,
            sourceRequirementIds: [],
            status: product && isHardIoCapabilityEligible(product) ? "accepted" : "needs_review",
            why: `Overridden by ${input.override.id}: ${input.override.reason}`,
            how: "Scoped override forced an extra controller after base deterministic controller allocation.",
            sourceRefs,
            ruleTraceRefs,
            risk: product && isHardIoCapabilityEligible(product)
                ? NO_RISK
                : {
                    level: "blocker",
                    riskRefs: [`risk-${id}-product`],
                    notes: "Override selected an unknown or unreviewed controller product."
                }
        }, input.override);
        controllers.push(controller);
    }
    return markSolution({
        ...input.solution,
        controllers
    }, input.override);
}
function isDefaultEdgeRemoval(input) {
    return (input.change.delta < 0 &&
        isDefaultEdgeRemovalSelector(input.change.selector) &&
        shouldInjectDefaultEdgeRows(input.solution));
}
function markDefaultEdgeRemoval(input) {
    const productPartNo = input.change.selector.productPartNo?.toUpperCase();
    if (!productPartNo) {
        return input.solution;
    }
    const targetRef = defaultEdgeRemovalTargetRef(productPartNo);
    const justification = {
        id: `justification-${input.solution.apartmentTypeId}-${safeId(targetRef)}`,
        targetRef,
        why: `Default Edge item ${productPartNo} was removed by scoped override ${input.override.id}.`,
        how: "The generated solution had no explicit Edge infrastructure row, so the override marks the no-trigger viewmodel default as removed instead of creating a blocking missing-target risk.",
        assumptions: [
            "Only the no-trigger default Edge row is suppressed; explicit Edge infrastructure still uses the normal infrastructure quantity path."
        ],
        sourceRefs: input.override.sourceRefs,
        ruleTraceRefs: [overrideTraceRef(input.override)],
        createdAt: ENGINE_TIMESTAMP
    };
    return markSolution({
        ...input.solution,
        justifications: [
            ...input.solution.justifications.filter((candidate) => candidate.targetRef !== targetRef),
            justification
        ]
    }, input.override);
}
function addScreenDevice(input) {
    const product = getScreenDeviceProduct(input.screenKind);
    const controller = input.solution.controllers[0];
    const zone = input.solution.logicalZones[0];
    const deviceId = `device-${input.solution.apartmentTypeId}-override-${safeId(input.override.id)}-${safeId(input.screenKind)}-${input.solution.devices.length + 1}`;
    const screenMapping = deriveScreenMapping({
        id: deviceId,
        kind: input.screenKind,
        zoneId: zone?.id ?? input.solution.apartmentTypeId,
        ...(controller ? { controllerId: controller.id } : {})
    });
    const sourceRefs = uniqueSourceRefs([
        ...input.override.sourceRefs,
        ...(zone?.sourceRefs ?? [])
    ]);
    const ruleTraceRefs = uniqueRuleTraceRefs([
        product.ruleTraceId,
        ...screenMapping.traces.map((trace) => trace.id),
        overrideTraceRef(input.override),
        ...(zone?.ruleTraceRefs ?? [])
    ]);
    const device = markOverridden({
        id: deviceId,
        productPartNo: product.productPartNo,
        family: product.family,
        role: product.role,
        quantity: 1,
        ...(zone ? { ownerRef: zone.id } : {}),
        pointDemandRefs: zone
            ? input.solution.pointDemands
                .filter((demand) => demand.ownerRef === zone.id)
                .map((demand) => demand.id)
            : [],
        why: `Overridden by ${input.override.id}: ${input.override.reason}`,
        how: "Scoped override added a UI screen after base derivation and before validation.",
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    }, input.override);
    const mapping = makeScreenDeviceMapping({
        id: `device-map-${deviceId}`,
        deviceRef: deviceId,
        screenKind: input.screenKind,
        screenMapping,
        override: input.override,
        sourceRefs,
        ruleTraceRefs,
        ...(zone ? { zoneId: zone.id } : {})
    });
    return markSolution({
        ...input.solution,
        devices: [...input.solution.devices, device],
        mappings: [...input.solution.mappings, mapping]
    }, input.override);
}
function substituteScreenMapping(input) {
    const screenMapping = deriveScreenMapping({
        id: input.mapping.deviceRef,
        kind: input.screenKind,
        zoneId: input.mapping.zoneId ??
            input.solution.devices.find((device) => device.id === input.mapping.deviceRef)
                ?.ownerRef ??
            input.solution.apartmentTypeId,
        ...(input.mapping.logicalControllerRef
            ? { controllerId: input.mapping.logicalControllerRef }
            : input.solution.controllers[0]
                ? { controllerId: input.solution.controllers[0].id }
                : {})
    });
    return makeScreenDeviceMapping({
        id: input.mapping.id,
        deviceRef: input.mapping.deviceRef,
        screenKind: input.screenKind,
        screenMapping,
        override: input.override,
        sourceRefs: input.mapping.sourceRefs,
        ruleTraceRefs: input.mapping.ruleTraceRefs,
        ...(input.mapping.zoneId ? { zoneId: input.mapping.zoneId } : {}),
        ...(input.mapping.roomId ? { roomId: input.mapping.roomId } : {})
    });
}
function makeScreenDeviceMapping(input) {
    const product = getScreenDeviceProduct(input.screenKind);
    const isDirectWired = input.screenMapping.mappingType === "direct_wired";
    return markOverridden({
        id: input.id,
        deviceRef: input.deviceRef,
        ...(input.screenMapping.controllerId
            ? { logicalControllerRef: input.screenMapping.controllerId }
            : {}),
        ...(isDirectWired && input.screenMapping.controllerId
            ? { physicalTerminationRef: input.screenMapping.controllerId }
            : {}),
        ...(input.roomId ? { roomId: input.roomId } : {}),
        ...(input.zoneId ? { zoneId: input.zoneId } : {}),
        mappingType: input.screenMapping.mappingType,
        cableImplication: isDirectWired
            ? "RDU-Touch is hard-wired 1:1 to the controller serving this zone."
            : "TS-4/TS-7 is networked and paired in software; it is not a 1:1 controller wiring constraint.",
        commissioningNote: isDirectWired
            ? "Verify the RDU direct cable lands at the controller serving this zone."
            : `Pair ${product.productPartNo} to ${input.screenMapping.controllerId ?? "the selected controller"} for the served zone during commissioning.`,
        why: isDirectWired
            ? "RDU topology is a hard wiring constraint."
            : "Network screens can pair to any controller, so the controller reference is a logical pairing, not a hardwired termination.",
        how: "Scoped override re-derived the UI mapping through @automatry/rules deriveScreenMapping.",
        sourceRefs: uniqueSourceRefs([...input.sourceRefs, ...input.override.sourceRefs]),
        ruleTraceRefs: uniqueRuleTraceRefs([
            ...input.ruleTraceRefs,
            ...input.screenMapping.traces.map((trace) => trace.id),
            overrideTraceRef(input.override)
        ]),
        risk: NO_RISK
    }, input.override);
}
function recalculateControllerLedgers(solution) {
    const controllers = solution.controllers.map((controller) => ({
        ...controller,
        pointLedger: buildControllerPointLedger({
            controllerId: controller.id,
            pointDemands: solution.pointDemands,
            zoneSlots: controller.zoneSlots,
            capacity: controller.capacity,
            sourceRefs: controller.sourceRefs,
            ruleTraceRefs: controller.ruleTraceRefs
        })
    }));
    return {
        ...solution,
        controllers,
        pointLedger: buildApartmentPointLedger({
            apartmentTypeId: solution.apartmentTypeId,
            pointDemands: solution.pointDemands,
            controllers,
            sourceRefs: solution.sourceRefs,
            ruleTraceRefs: solution.ruleTraceRefs
        })
    };
}
function addBlockingOverrideRisk(input) {
    const riskId = `risk-${input.solution.apartmentTypeId}-${safeId(input.override.id)}-override`;
    const risk = {
        id: riskId,
        projectId: input.projectId,
        type: "other",
        severity: "blocker",
        scope: {
            level: "apartment_type",
            refId: input.solution.apartmentTypeId
        },
        title: "Scoped override could not be applied safely",
        plainEnglish: input.message,
        technicalTrace: input.override.sourceRefs,
        recommendedActions: [
            {
                action: "Revise the scoped override target or structured change.",
                owner: "user",
                status: "open"
            }
        ],
        status: "open",
        blocksExport: true,
        sourceRefs: input.override.sourceRefs,
        ruleTraceRefs: [overrideTraceRef(input.override)],
        createdAt: ENGINE_TIMESTAMP
    };
    return markSolution({
        ...input.solution,
        risks: [...input.solution.risks, risk],
        risk: {
            level: "blocker",
            riskRefs: [...input.solution.risk.riskRefs, riskId],
            notes: input.message
        }
    }, input.override);
}
function markSolution(solution, override) {
    return markOverridden(solution, override);
}
function markOverridden(item, override) {
    const overrideText = `Overridden by ${override.id}: ${override.reason}`;
    return {
        ...item,
        why: appendSentence(item.why, overrideText),
        how: appendSentence(item.how, `Override intent: ${override.intent}`),
        sourceRefs: uniqueSourceRefs([...item.sourceRefs, ...override.sourceRefs]),
        ruleTraceRefs: uniqueRuleTraceRefs([
            ...item.ruleTraceRefs,
            overrideTraceRef(override)
        ])
    };
}
function matchesProductSubstitutionSelector(item, change) {
    return (matchesSelector(item, change.selector) &&
        (change.fromProductPartNo === undefined ||
            item.productPartNo === change.fromProductPartNo));
}
function matchesInfrastructureSelector(item, selector) {
    return (matchesSelector(item, selector) &&
        (selector.itemKind === undefined || item.itemKind === selector.itemKind));
}
function matchesSelector(item, selector) {
    if (selector.id !== undefined && item.id !== selector.id) {
        return false;
    }
    if (selector.productPartNo !== undefined &&
        item.productPartNo !== selector.productPartNo) {
        return false;
    }
    if (selector.role !== undefined && item.role !== selector.role) {
        return false;
    }
    return true;
}
function inferScreenKind(productPartNo) {
    const normalized = productPartNo.toUpperCase();
    if (normalized === "TP-UI-TS-4") {
        return "ts4";
    }
    if (normalized === "TP-UI-TS-7") {
        return "ts7";
    }
    if (normalized.startsWith("RDU-TOUCH")) {
        return "rdu";
    }
    return undefined;
}
function isInfrastructureItemKind(itemKind) {
    return (itemKind !== undefined &&
        [
            "netmaster",
            "panel",
            "enclosure",
            "power_supply",
            "switch",
            "gateway",
            "edge_compute",
            "network_switch",
            "edge_enclosure",
            "cloud_service",
            "metering_unit",
            "relay",
            "io_module",
            "cabling_note",
            "other"
        ].includes(itemKind));
}
function buildProductQuantityChanges(before, after) {
    const productRefs = [
        ...new Set([
            ...Object.keys(before.totals.productQuantities),
            ...Object.keys(after.totals.productQuantities)
        ])
    ].sort();
    return productRefs.flatMap((productRef) => {
        const beforeQuantity = before.totals.productQuantities[productRef] ?? 0;
        const afterQuantity = after.totals.productQuantities[productRef] ?? 0;
        if (beforeQuantity === afterQuantity) {
            return [];
        }
        const delta = afterQuantity - beforeQuantity;
        return [
            {
                kind: "product_quantity",
                subjectRef: productRef,
                before: beforeQuantity,
                after: afterQuantity,
                delta,
                summary: `${productRef} ${formatDelta(delta)} (${beforeQuantity} -> ${afterQuantity})`
            }
        ];
    });
}
function buildValidationStatusChanges(before, after) {
    if (before.validation.status === after.validation.status) {
        return [];
    }
    return [
        {
            kind: "validation_status",
            subjectRef: after.id,
            before: before.validation.status,
            after: after.validation.status,
            summary: `validation ${before.validation.status} -> ${after.validation.status}`
        }
    ];
}
function buildRiskCountChanges(before, after) {
    if (before.totals.riskCount === after.totals.riskCount) {
        return [];
    }
    const delta = after.totals.riskCount - before.totals.riskCount;
    return [
        {
            kind: "risk_count",
            subjectRef: after.id,
            before: before.totals.riskCount,
            after: after.totals.riskCount,
            delta,
            summary: `risk count ${formatDelta(delta)} (${before.totals.riskCount} -> ${after.totals.riskCount})`
        }
    ];
}
function formatDelta(delta) {
    return delta > 0 ? `+${delta}` : String(delta);
}
function appendSentence(text, sentence) {
    return text.includes(sentence) ? text : `${text} ${sentence}`;
}
function overrideTraceRef(override) {
    return `override-${override.id}`;
}
function uniqueRuleTraceRefs(ruleTraceRefs) {
    return [...new Set(ruleTraceRefs)];
}
function safeId(value) {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}
//# sourceMappingURL=overrideApplication.js.map